import React from 'react';

import { flexRender, type Table } from '@tanstack/react-table';
import _ from 'lodash';
import { ArrowDown, ArrowUp } from 'lucide-react';

import cn from '../utils/cn';

import Spinner from './Spinner';

type TanstackTableProps<T> = {
  table: Table<T>;
  isLoading: boolean;
  emptyState: React.ReactNode;
  handleSelect?: {
    lockColIndex?: number;
    onClick: (id: number) => void;
  };
};

const TanstackTable = <T,>({
  table,
  isLoading,
  emptyState,
  handleSelect,
}: TanstackTableProps<T>) => (
  <div className="w-full h-full">
    <table className="w-full font-normal">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr
            key={headerGroup.id}
            className="!rounded-md border-[#E4E7EC] bg-[#F8F8F8] first:border-l last:border-r"
          >
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={cn(
                  'first:border-l border-t border-b last:border-r border-[#E4E7EC] px-6 py-3 text-start text-sm text-[#4D4D4D]'
                )}
              >
                <button
                  type="button"
                  onClick={header.column.getToggleSortingHandler()}
                  className="flex gap-1 items-start"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {{
                    asc: <ArrowUp size={20} />,
                    desc: <ArrowDown size={20} />,
                  }[header.column.getIsSorted() as string] ?? null}
                </button>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="relative">
        {isLoading && (
          <div
            className={cn(
              'absolute border-[#E4E7EC] h-[200px] border flex w-full items-center justify-center bg-gray-100 opacity-50',
              {
                'h-full': table.getRowModel().rows.length > 0,
              }
            )}
          >
            <Spinner />
          </div>
        )}
        {table.getRowModel().rows?.map((row) => (
          <tr
            key={row.id}
            className="cursor-pointer hover:bg-[#F8F8F8] py-3 transition"
          >
            {row.getVisibleCells().map((cell, index) => (
              <td
                key={cell.id}
                onClick={() => {
                  const rowId = (row.original as { id: number }).id ?? NaN;

                  _.isNumber(rowId) &&
                    index !== handleSelect?.lockColIndex &&
                    handleSelect?.onClick(rowId);
                }}
                className="border-b border-[#E4E7EC] px-6 text-[#333] py-3 first:border-l last:border-r hover:bg-[#F8F8F8] transition"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {!table.getRowModel().rows.length && !isLoading && (
      <div className="flex justify-center border border-t-0 border-[#E4E7EC] py-20">
        <p className="text-lg text-[#999] font-normal">{emptyState}</p>
      </div>
    )}
  </div>
);

export default TanstackTable;
